import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import Nav from "@/components/Nav";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Paths from "@/components/Paths";
import ScrollStory from "@/components/ScrollStory";
import Indicator from "@/components/Indicator";
import CaseStudies from "@/components/CaseStudies";
import Education from "@/components/Education";
import Community from "@/components/Community";
import DailyFeed from "@/components/DailyFeed";
import Testimonials from "@/components/Testimonials";
import WhyAtlas from "@/components/WhyAtlas";
import FounderStory from "@/components/FounderStory";
import Exclusive from "@/components/Exclusive";
import Pricing from "@/components/Pricing";
import Milestone from "@/components/Milestone";
import BrokerPerk from "@/components/BrokerPerk";
import Faq from "@/components/Faq";
import Footer from "@/components/Footer";
import Preloader from "@/components/Preloader";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Giveaway from "@/pages/Giveaway";
import PositionSizing from "@/pages/PositionSizing";
import { AnimatePresence } from "framer-motion";
import { scrollToSection } from "@/lib/scroll";

function Home() {
  const [intro, setIntro] = useState(() => !sessionStorage.getItem("atlas_intro_seen"));
  const location = useLocation();

  useEffect(() => {
    if (intro) {
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
    }
  }, [intro]);

  useEffect(() => {
    const target = location.state?.scrollTo;
    if (target && !intro) {
      const t = setTimeout(() => scrollToSection(target), 150);
      window.history.replaceState({}, "");
      return () => clearTimeout(t);
    }
  }, [location.state, intro]);

  return (
    <div className="noise bg-ink text-slate-200 min-h-screen" data-testid="home-page">
      <AnimatePresence>
        {intro && (
          <Preloader
            key="intro"
            onDone={() => {
              sessionStorage.setItem("atlas_intro_seen", "1");
              setIntro(false);
            }}
          />
        )}
      </AnimatePresence>
      <Nav />
      <main>
        {intro ? <div className="min-h-screen" /> : <Hero />}
        <Marquee />
        <Paths />
        <ScrollStory />
        <Indicator />
        <CaseStudies />
        <Education />
        <Community />
        <DailyFeed />
        <Testimonials />
        <WhyAtlas />
        <FounderStory />
        <Exclusive />
        <Pricing />
        <Milestone />
        <BrokerPerk />
        <Faq />
      </main>
      <Footer />
    </div>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/giveaway" element={<Giveaway />} />
        <Route path="/academy/position-sizing" element={<PositionSizing />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
      </Routes>
      <Toaster theme="dark" position="top-center" />
    </BrowserRouter>
  );
}

export default App;
